import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';

function NavLink({
  to,
  label,
  active,
}: {
  to: string;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      className={`transition ${
        active ? 'font-medium text-black' : 'text-gray-600 hover:text-black'
      }`}
    >
      {label}
    </Link>
  );
}

const LEADER_TAB = {
  to: '/dashboard',
  label: 'Dashboard',
  icon: (active: boolean) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#000' : '#bbb'} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="8" height="9" rx="1"/>
      <rect x="14" y="3" width="8" height="5" rx="1"/>
      <rect x="14" y="12" width="8" height="9" rx="1"/>
      <rect x="2" y="16" width="8" height="5" rx="1"/>
    </svg>
  ),
  match: (p: string) => p === '/dashboard',
};

const NAV_ITEMS = [
  {
    to: '/today',
    label: 'Today',
    icon: (active: boolean) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke={active ? '#000' : '#bbb'} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    match: (p: string) => p === '/today',
  },
  {
    to: '/explore',
    label: 'Explore',
    icon: (active: boolean) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke={active ? '#000' : '#bbb'} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
    match: (p: string) => p === '/explore' || p.startsWith('/clubs'),
  },
  {
    to: '/my-clubs',
    label: 'My Clubs',
    icon: (active: boolean) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke={active ? '#000' : '#bbb'} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    match: (p: string) => p === '/my-clubs',
  },
  {
    to: '/profile',
    label: 'Profile',
    icon: (active: boolean) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke={active ? '#000' : '#bbb'} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    match: (p: string) => p === '/profile',
  },
];

function BottomNav() {
  const location = useLocation();
  const { leader } = useAuth();
  const tabs = leader ? [...NAV_ITEMS, LEADER_TAB] : NAV_ITEMS;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around border-t border-black/8 bg-white/90 backdrop-blur-xl md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)', height: 'calc(64px + env(safe-area-inset-bottom))' }}
    >
      {tabs.map(({ to, label, icon, match }) => {
        const active = match(location.pathname);
        return (
          <Link
            key={to}
            to={to}
            className="flex flex-col items-center gap-1 px-3 py-2"
          >
            {icon(active)}
            <span
              className={`text-[10px] font-semibold tracking-wide transition ${
                active ? 'text-black' : 'text-[#bbb]'
              }`}
            >
              {label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}

export default function Navbar() {
  const location = useLocation();
  const { user, profile, leader } = useAuth();
  const [pendingClaims, setPendingClaims] = useState(0);

  useEffect(() => {
    if (!leader?.is_admin) return;
    supabase
      .from('club_ownership_claims')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'pending')
      .then(({ count }) => setPendingClaims(count ?? 0));
  }, [leader]);

  const avatar = profile?.profile_image || user?.user_metadata?.avatar_url || null;

  const displayLetter =
    profile?.display_name?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    'U';

  const isToday = location.pathname === '/today';
  const isExplore =
    location.pathname === '/explore' ||
    location.pathname === '/clubs';
  const isMyClubs = location.pathname === '/my-clubs';
  const isAdmin = location.pathname === '/admin';

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-black/5 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center transition hover:opacity-90">
            <img
              src="https://mxexasojrmqlgvguujzb.supabase.co/storage/v1/object/public/club-assets/logos/runcollogo.svg"
              alt="Run Collective"
              className="h-10 md:h-12 w-auto max-w-[200px] object-contain"
            />
          </Link>

          <div className="hidden md:flex items-center gap-6 text-sm">
            <NavLink to="/my-clubs" label="My Clubs" active={isMyClubs} />
            <NavLink to="/today" label="Today" active={isToday} />
            <NavLink to="/explore" label="Explore" active={isExplore} />
            {leader && <NavLink to="/dashboard" label="Dashboard" active={location.pathname === '/dashboard'} />}
            {leader?.is_admin && (
              <div className="relative">
                <NavLink to="/admin" label="Admin" active={isAdmin} />
                {pendingClaims > 0 && (
                  <span className="absolute -right-3 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                    {pendingClaims}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!user ? (
              <>
                <Link
                  to="/auth"
                  className="text-sm text-gray-600 hover:text-black transition"
                >
                  Sign in
                </Link>

                <Link
                  to="/auth"
                  className="rounded-xl bg-black px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition"
                >
                  Join
                </Link>
              </>
            ) : (
              <Link
                to="/profile"
                className="group flex items-center md:flex"
                aria-label="Go to profile"
              >
                {avatar ? (
                  <img
                    src={avatar}
                    alt="Profile"
                    className="h-9 w-9 rounded-full object-cover border border-black/10 transition group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-xs font-medium text-white transition group-hover:scale-105">
                    {displayLetter}
                  </div>
                )}
              </Link>
            )}
          </div>
        </div>
      </header>

      <BottomNav />
    </>
  );
}
